import { useState } from 'react'
import { match as MatchProps } from 'react-router-dom'
import {
  JsonParam,
  NumberParam,
  StringParam,
  ArrayParam,
  useQueryParams,
  withDefault,
} from 'use-query-params'
import { useModal } from '@ebay/nice-modal-react'

import {
  useGlobalState,
  useMessage,
  useProjectDraftList,
  usePostBulkDocsDownload,
} from '@pepper/ui'

import DraftsList from 'components/drafts/List/List'
import { sortOptions } from 'components/items/Header/schema'
import { useSelectedCompanyId } from 'utils/functions/misc'

import BulkDownloadModal from '../../BulkDownloadModal/BulkDownloadModal'
import {
  setBulkDownloadFormLS,
  showBulkDocsDownloadForm,
} from '../../BulkDownloadModal/BulkDownloadModal.utils'
import s from './Items.module.css'
import { RenderedList } from './RenderedList'

type Props = {
  match: MatchProps<{ projectId: string; tab?: string }>
  project?: Project
}

const PAGE_SIZE = 25

const Items = ({ match, project }: Props) => {
  const [showDraftBanner, setShowDraftBanner] = useState(true)
  const { projectId, tab } = match.params
  const companyId = useSelectedCompanyId()
  const { showError } = useMessage()
  const { setBulkDownloadJob, permissions } = useGlobalState()
  const bulkDownloadModal = useModal(BulkDownloadModal)
  const bulkDocsDownload = usePostBulkDocsDownload()

  const [queryParamFilters, setQueryParamFilters] = useQueryParams({
    search_title: withDefault(StringParam, undefined),
    item_types: withDefault(StringParam, undefined),
    stage: withDefault(ArrayParam, undefined),
    rework: withDefault(StringParam, undefined),
    vertical: withDefault(ArrayParam, undefined),
    sort: withDefault(JsonParam, {
      sort_by: sortOptions[0].sortBy,
      sort_order: sortOptions[0].sortOrder,
    }),
    status: withDefault(ArrayParam, []),
    assignee: withDefault(ArrayParam, []),
    currentPage: withDefault(NumberParam, 0),
    tab: withDefault(StringParam, undefined),
  })
  const showDrafts = queryParamFilters.tab === 'draft'
  const hasMarketplacePermission = permissions?.marketplace
  const showOrders =
    (hasMarketplacePermission && !queryParamFilters.item_types) ||
    queryParamFilters.item_types === 'assignment'

  const {
    records: projectDrafts,
    refetch: refetchProjectsDraft,
    totalRecords: totalDrafts,
  } = useProjectDraftList('Project Draft', {
    page_size: PAGE_SIZE,
    company_id: companyId,
    project_id: projectId,
    vertical: queryParamFilters?.vertical?.map(item => Number(item)),
    sort_by: queryParamFilters.sort?.sort_by,
    sort_order: queryParamFilters.sort?.sort_order,
  })

  const toggleDraftsView = () => {
    setQueryParamFilters({
      tab: showDrafts ? undefined : 'draft',
      sort: undefined,
      stage: undefined,
      vertical: undefined,
      rework: undefined,
      item_types: undefined,
      currentPage: undefined,
    })
    setShowDraftBanner(!showDraftBanner)
  }

  const handleDocsDownload = (
    _items: Item[] | Assignment[],
    itemType: 'assignment' | 'private_doc'
  ) => {
    if (showBulkDocsDownloadForm(companyId)) {
      bulkDownloadModal.show({
        handleSubmit: purpose => {
          handleDownloadDocs(_items, purpose, itemType)
        },
      })
    } else {
      handleDownloadDocs(_items, [], itemType)
    }
  }

  const handleDownloadDocs = (
    _items: Item[] | Assignment[],
    purpose: {
      purpose: string
      message?: string
    }[],
    itemType: 'assignment' | 'private_doc'
  ) => {
    const projectName = project?.project_title ?? 'Project'

    bulkDocsDownload.mutate(
      {
        companyId,
        body: {
          data: _items.map(item => ({
            id: item.id,
            item_type: itemType,
          })),
          export_purpose: purpose,
        },
      },
      {
        onSuccess(data) {
          setBulkDownloadJob({
            id: data.id,
            name: projectName,
            companyId,
            count: _items.length,
            status: 'pending',
          })
          setBulkDownloadFormLS({ companyId })
          bulkDownloadModal.hide()
        },
        onError(error) {
          if (error instanceof Error) {
            showError(error.message)
          }
        },
      }
    )
  }

  return (
    <div className={s.wrapper}>
      {showDrafts ? (
        <DraftsList
          projectDrafts={projectDrafts}
          totalDrafts={totalDrafts}
          queryParamFilters={queryParamFilters}
          setQueryParamFilters={setQueryParamFilters}
          toggleDraftsView={toggleDraftsView}
          refetchDrafts={refetchProjectsDraft}
          projectId={projectId}
        />
      ) : (
        <RenderedList
          showOrders={showOrders}
          projectDrafts={projectDrafts}
          handleDocsDownload={handleDocsDownload}
          toggleDraftsView={toggleDraftsView}
          project={project}
        />
      )}
    </div>
  )
}

export default Items
